// DLC_09 · Skrzynki e-Doręczeń i certyfikaty — panel CEO.
//
// Tu widać, czy połączenie żyje i kiedy umiera certyfikat (wygasły certyfikat to klasyczna
// cicha śmierć takiej integracji). Stąd też pobiera się plik CSR, który administrator
// skrzynki wgrywa u operatora — klucz prywatny zostaje na serwerze i nigdy nie idzie przez API.
import { useState } from "react";
import { Download, PlugZap, ShieldCheck, Upload } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { Spinner } from "@/components/ui/Spinner";
import {
  EmptyState,
  HubNav,
  PageHeader,
  SectionCard,
  TableShell,
  Th,
} from "@/features/power_projects/components/ui";
import { fmtDateTime } from "@/features/power_projects/utils";
import { downloadMailboxCsr } from "../api";
import { DLC09_HUB_LINKS } from "../components/ui";
import { useEdoreczeniaSummary, useMailboxMutations, useMailboxes } from "../hooks";
import type { Mailbox } from "../types";

function CertificateCell({ mailbox }: { mailbox: Mailbox }) {
  const days = mailbox.certificate_days_left;
  if (!mailbox.certificate_expires_at) {
    return <span className="text-[11px] text-[var(--color-text-muted)]">brak certyfikatu</span>;
  }
  const tone =
    days === null ? "" : days < 0 ? "text-red-600" : days <= 30 ? "text-amber-600" : "";
  return (
    <div className={`text-[11px] ${tone}`}>
      do {fmtDateTime(mailbox.certificate_expires_at)}
      {days !== null && (
        <div>{days < 0 ? "wygasł" : `pozostało ${days} dni`}</div>
      )}
    </div>
  );
}

function MailboxRow({ mailbox }: { mailbox: Mailbox }) {
  const { testConnection, uploadCertificate } = useMailboxMutations();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const getCsr = async (confirmReplace: boolean) => {
    setBusy(true);
    setMessage("");
    try {
      const blob = await downloadMailboxCsr(mailbox.id, confirmReplace);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `csr-${mailbox.ade_address}.pem`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      setMessage(
        status === 409
          ? "Skrzynka ma już klucz. Ponowne wygenerowanie unieważni obecny certyfikat — kliknij „Wygeneruj mimo to”."
          : "Nie udało się wygenerować wniosku o certyfikat.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr className="border-t border-[var(--color-border)] align-top">
      <td className="px-2 py-1.5">
        <div>{mailbox.label || mailbox.company_name}</div>
        <div className="font-mono text-[10px] text-[var(--color-text-muted)]">
          {mailbox.ade_address}
        </div>
      </td>
      <td className="px-2 py-1.5 text-[11px]">{mailbox.company_name}</td>
      <td className="px-2 py-1.5 text-[11px]">{mailbox.environment_display}</td>
      <td className="px-2 py-1.5 text-[11px]">
        {mailbox.connection_state_display}
        {mailbox.last_connection_error && (
          <div className="text-red-600">{mailbox.last_connection_error}</div>
        )}
        {mailbox.last_connection_check_at && (
          <div className="text-[var(--color-text-muted)]">
            {fmtDateTime(mailbox.last_connection_check_at)}
          </div>
        )}
      </td>
      <td className="px-2 py-1.5">
        <CertificateCell mailbox={mailbox} />
      </td>
      <td className="px-2 py-1.5 text-right">
        <div className="flex flex-col items-end gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => testConnection.mutate(mailbox.id)}
            disabled={testConnection.isPending}
          >
            <PlugZap size={14} /> Test połączenia
          </Button>
          <Button size="sm" variant="ghost" onClick={() => getCsr(false)} disabled={busy}>
            <Download size={14} /> Wniosek o certyfikat (CSR)
          </Button>
          {mailbox.has_private_key && (
            <Button size="sm" variant="ghost" onClick={() => getCsr(true)} disabled={busy}>
              Wygeneruj mimo to
            </Button>
          )}
          <div className="w-56">
            <FileDropZone
              label="Wgraj wydany certyfikat"
              accept=".pem,.crt,.cer"
              onSelect={(files) =>
                files[0] && uploadCertificate.mutate({ id: mailbox.id, file: files[0] })
              }
            />
          </div>
          {message && <div className="max-w-64 text-[11px] text-amber-700">{message}</div>}
        </div>
      </td>
    </tr>
  );
}

export function MailboxesPage() {
  const { data: summary } = useEdoreczeniaSummary();
  const { data: mailboxes, isLoading } = useMailboxes();

  return (
    <div>
      <PageHeader
        title="Skrzynki e-Doręczeń"
        subtitle="Adresy, certyfikaty i stan połączenia — po jednej skrzynce na spółkę"
      />
      <HubNav
        links={DLC09_HUB_LINKS}
        badges={{
          "/edoreczenia": summary?.queued ?? 0,
          "/edoreczenia/skrzynka": summary?.inbox_new ?? 0,
        }}
      />

      <SectionCard className="mb-5">
        <div className="text-[11px] text-[var(--color-text-secondary)]">
          <ShieldCheck size={12} className="mr-1 inline" />
          Uruchomienie skrzynki: pobierz wniosek o certyfikat (CSR) i przekaż go administratorowi
          skrzynki — wgrywa go w module uprawnień operatora po zalogowaniu przez Węzeł Krajowy.
          Wydany certyfikat wgraj tutaj. Klucz prywatny zostaje na serwerze i nigdy nie jest
          udostępniany przez API.
          <div className="mt-1">
            <Upload size={12} className="mr-1 inline" />
            Do czasu otrzymania dostępu do środowiska testowego skrzynka może pracować w trybie
            udawanym — cały obieg działa, nic nie wychodzi na zewnątrz.
          </div>
        </div>
      </SectionCard>

      {isLoading ? (
        <Spinner />
      ) : !mailboxes?.length ? (
        <EmptyState>Brak skonfigurowanych skrzynek.</EmptyState>
      ) : (
        <TableShell>
          <thead>
            <tr>
              <Th>Skrzynka</Th>
              <Th>Spółka</Th>
              <Th>Środowisko</Th>
              <Th>Połączenie</Th>
              <Th>Certyfikat</Th>
              <Th> </Th>
            </tr>
          </thead>
          <tbody>
            {mailboxes.map((m) => (
              <MailboxRow key={m.id} mailbox={m} />
            ))}
          </tbody>
        </TableShell>
      )}
    </div>
  );
}
